// DEPENDENCIES
import React, { useState } from "react";

// MUI COMPONENTS
import { Button, IconButton, Popover, Tooltip } from "@mui/material";

// MUI ICONS
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// HELPERS
import { MONTHS, isoOf, mondayOf, monthGrid, sameDay } from "./timeEntryUtils";

// Month grid with prev/next month, used in the web Popover and the mobile
// bottom sheet. mode "week" highlights the whole selected week as a band;
// mode "day" highlights just the selected date. A dot marks days that
// already have time logged. It opens on the selected date's month each time
// it mounts.
export function MonthCalendarPanel({
	mode,
	selectedDate,
	today,
	hasHours,
	onPick,
	footerLabel,
	onFooter,
}) {
	// STATES
	const [month, setMonth] = useState(
		() => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
	);

	const selectedWeek = isoOf(mondayOf(selectedDate));
	const step = (n) =>
		setMonth((m) => new Date(m.getFullYear(), m.getMonth() + n, 1));

	// RENDER
	return (
		<div className="teCal-panel">
			<div className="teCal-head">
				<Tooltip title="Previous month" arrow placement="top">
					<IconButton size="small" onClick={() => step(-1)}>
						<ChevronLeftIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<span className="teCal-title">
					{MONTHS[month.getMonth()]} {month.getFullYear()}
				</span>
				<Tooltip title="Next month" arrow placement="top">
					<IconButton size="small" onClick={() => step(1)}>
						<ChevronRightIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</div>
			<div className="teCal-grid">
				{["M", "T", "W", "T", "F", "S", "S"].map((h, i) => (
					<span key={i} className="teCal-dow">
						{h}
					</span>
				))}
				{monthGrid(month).map((d, k) => {
					const col = k % 7;
					const inWeek =
						mode === "week" && isoOf(mondayOf(d)) === selectedWeek;
					const on =
						mode === "week" ? inWeek : sameDay(d, selectedDate);
					const cls = [
						"teCal-day",
						on && "teCal-day--on",
						inWeek && col === 0 && "teCal-day--bandStart",
						inWeek && col === 6 && "teCal-day--bandEnd",
						inWeek && col > 0 && col < 6 && "teCal-day--band",
						d.getMonth() !== month.getMonth() &&
							"teCal-day--outside",
						sameDay(d, today) && "teCal-day--today",
					]
						.filter(Boolean)
						.join(" ");
					return (
						<button
							type="button"
							key={k}
							className={cls}
							onClick={() => onPick(d)}
						>
							<span>{d.getDate()}</span>
							<span
								className={
									"teCal-dot" +
									(hasHours(d) ? " teCal-dot--on" : "")
								}
							/>
						</button>
					);
				})}
			</div>
			<div className="teCal-foot">
				<span className="teLegend">
					<span className="teLegend-dot teLegend-dot--blue" />
					has time logged
				</span>
				<Button variant="outlined" size="small" onClick={onFooter}>
					{footerLabel}
				</Button>
			</div>
		</div>
	);
}

// Web: the month grid in a Popover anchored to the date button
export default function MonthCalendar({
	anchorEl,
	onClose,
	anchorOrigin = { vertical: "bottom", horizontal: "left" },
	transformOrigin = { vertical: "top", horizontal: "left" },
	...panelProps
}) {
	return (
		<Popover
			open={Boolean(anchorEl)}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={anchorOrigin}
			transformOrigin={transformOrigin}
			PaperProps={{ className: "teCal" }}
		>
			<MonthCalendarPanel {...panelProps} />
		</Popover>
	);
}
